import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import { paperClasses } from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import {
  Account_Read_Typeahead,
  AccountType,
  getV1CompaniesAccountsTypeahead,
  ModelError_Item,
  ModelError_Response,
  OrderUnitOfMeasure,
} from '@treadinc/horizon-api-spec';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import AsyncAutocompleteField from '~components/FormFields/v2/Autocomplete/AsyncAutocompleteField';
import {
  AutocompleteFieldOption,
  FetchFnQueryArgument,
} from '~components/FormFields/v2/Autocomplete/types';
import NumberInput from '~components/FormFields/v2/NumberInput';
import SelectInput from '~components/FormFields/v2/SelectInput';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { AccountTypeahead } from '~hooks/useAccount/models';
import { useAllowedUnitOfMeasureOptions } from '~hooks/useAllowedUnitOfMeasureOptions';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { MaterialRate } from '~hooks/useMaterialRates/models';
import useMaterialRates from '~hooks/useMaterialRates/useMaterialRates';
import { Site } from '~hooks/useSites/models';
import MaterialRateForm, {
  MaterialRateFormHandler,
} from '~pages/Settings/RatesManagement/MaterialRates/components/MaterialRateForm';
import { materialRatePriceToPayload } from '~pages/Settings/RatesManagement/MaterialRates/components/materialRateSchema';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

// The BE cannot filter project_id IS NULL, so project rows are dropped client-side.
// The cursor is exhausted before any empty state renders — a single filtered page
// could be all project rows and would otherwise show a false "no rates". The loop
// is capped defensively; server-side default_only / customer filters keep real
// result sets far below this.
const RATES_MAX_PAGES = 5;

interface CustomerFilterFormValues {
  customer: AutocompleteFieldOption<AccountTypeahead> | null;
}

interface InlineRateEditFormValues {
  buyPrice: string;
  sellPrice: string;
  unitOfMeasure: string;
}

interface RatesListState {
  isCapped: boolean;
  isLoading: boolean;
  rates: MaterialRate[];
}

interface AddRateDialogState {
  errors?: ModelError_Item[];
  isOpen: boolean;
  isSaving: boolean;
}

interface SiteMaterialRatesSectionProps {
  companyId: string;
  site: Site;
}

const SiteMaterialRatesSection = observer(function SiteMaterialRatesSection({
  companyId,
  site,
}: SiteMaterialRatesSectionProps) {
  const { toasterStore, userStore } = useStores();
  const {
    createMaterialRate,
    deleteMaterialRate,
    getCompanyMaterialRates,
    updateMaterialRate,
  } = useMaterialRates();
  const { currencyFormatter } = useCompanyCurrency();
  const unitOfMeasureOptions = useAllowedUnitOfMeasureOptions();

  const permissions = userStore.getPermissions();
  const ratesAreHiddenForThisUser = !permissions.canViewRate;

  const [listState, setListState] = useState<RatesListState>({
    isCapped: false,
    isLoading: true,
    rates: [],
  });
  const [editingRateId, setEditingRateId] = useState<string | null>(null);
  const [isSavingInlineEdit, setIsSavingInlineEdit] = useState(false);
  const [rateToDelete, setRateToDelete] = useState<MaterialRate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [addRateDialogState, setAddRateDialogState] = useState<AddRateDialogState>({
    isOpen: false,
    isSaving: false,
  });

  // A slow response for a previous filter must not overwrite the current one —
  // getCompanyMaterialRates takes no abort signal, so stale responses are dropped
  // by generation instead (same guard the dispatch order fetches use).
  const requestGenerationRef = useRef(0);

  const filterForm = useForm<CustomerFilterFormValues>({
    defaultValues: { customer: null },
  });
  const watchedCustomerOption = filterForm.watch('customer');
  const selectedCustomer = watchedCustomerOption?._entity ?? null;

  const inlineEditForm = useForm<InlineRateEditFormValues>({
    defaultValues: { buyPrice: '', sellPrice: '', unitOfMeasure: '' },
  });

  const materialRateFormRef = useRef<MaterialRateFormHandler>(null);

  const customersFetchFn = {
    arguments: { companyId },
    request: async ({
      query,
      signal,
      companyId,
    }: {
      query: FetchFnQueryArgument;
      signal: AbortSignal;
      companyId: string;
    }) => {
      return await getV1CompaniesAccountsTypeahead({
        path: { 'company-id': companyId },
        query: {
          'search[query]': query['search[query]'],
          'page[limit]': query['page[limit]'],
          'page[after]': query['page[after]'],
          'filter[account_types]': [AccountType.CUSTOMER],
        },
        signal,
      });
    },
  };

  const customerParseFn = (account: Account_Read_Typeahead) => {
    const parsed = AccountTypeahead.parse(account);

    return { label: parsed.name, value: parsed.id, _entity: parsed };
  };

  const fetchRates = useCallback(
    async (customerId: string | null) => {
      if (ratesAreHiddenForThisUser) {
        return;
      }

      const generation = ++requestGenerationRef.current;

      setListState((state) => ({ ...state, isLoading: true }));

      try {
        const collected: MaterialRate[] = [];
        let cursor: string | undefined;
        let pagesFetched = 0;

        do {
          const response = await getCompanyMaterialRates({
            companyId,
            customerId: customerId ?? undefined,
            defaultOnly: customerId ? undefined : true,
            siteIds: [site.id],
            pagination: { after: cursor, limit: MAX_API_PAGE_SIZE, page: 1 },
          });

          if (generation !== requestGenerationRef.current) {
            return;
          }

          collected.push(...response.data);
          cursor = response.pagination.after;
          pagesFetched += 1;
        } while (cursor && pagesFetched < RATES_MAX_PAGES);

        const nonProjectRates = collected.filter((rate) => isNil(rate.project));

        setListState({
          isCapped: Boolean(cursor),
          isLoading: false,
          rates: nonProjectRates,
        });
      } catch (error) {
        if (generation !== requestGenerationRef.current) {
          return;
        }

        // Keeping the previous scope's rows would render them under the failed
        // scope's heading with live edit/delete affordances — clear instead.
        setListState({ isCapped: false, isLoading: false, rates: [] });
        connection.handleRequestError(
          error,
          t('error_messages.material_rates.failed_to_fetch_material_rates'),
        );
      }
    },
    [companyId, getCompanyMaterialRates, ratesAreHiddenForThisUser, site.id],
  );

  // Mutations refetch through the LIVE filter value rather than a render-time
  // closure — an inline save started under customer A must not repopulate the
  // list with A's rows after the user has already switched to customer B.
  const refetchCurrentScope = useCallback(() => {
    return fetchRates(filterForm.getValues('customer')?._entity.id ?? null);
  }, [fetchRates, filterForm.getValues]);

  useEffect(() => {
    setEditingRateId(null);
    fetchRates(selectedCustomer?.id ?? null);
  }, [fetchRates, selectedCustomer?.id]);

  const startInlineEdit = useCallback(
    (rate: MaterialRate) => {
      inlineEditForm.reset({
        buyPrice: isNil(rate.buyPrice) ? '' : rate.buyPrice.toFixed(2),
        sellPrice: isNil(rate.sellPrice) ? '' : rate.sellPrice.toFixed(2),
        unitOfMeasure: rate.unitOfMeasure,
      });
      setEditingRateId(rate.id);
    },
    [inlineEditForm.reset],
  );

  const cancelInlineEdit = useCallback(() => {
    setEditingRateId(null);
  }, []);

  const saveInlineEdit = useCallback(
    async (rate: MaterialRate) => {
      inlineEditForm.clearErrors();

      const formValues = inlineEditForm.getValues();
      const buyPrice = String(formValues.buyPrice).trim();
      const sellPrice = String(formValues.sellPrice).trim();

      // Mirrors materialRateSchema's .min(0) so the inline path validates the
      // same floor the add-rate form does, without waiting on a BE 422.
      const priceIsNegative = (price: string) => price !== '' && Number(price) < 0;
      let hasNegativePrice = false;

      for (const [fieldName, price] of [
        ['buyPrice', buyPrice],
        ['sellPrice', sellPrice],
      ] as const) {
        if (priceIsNegative(price)) {
          inlineEditForm.setError(fieldName, {
            message: t('error_messages.material_rates.price_must_be_zero_or_more'),
          });
          hasNegativePrice = true;
        }
      }

      if (hasNegativePrice) {
        return;
      }

      setIsSavingInlineEdit(true);

      try {
        await updateMaterialRate(rate.id, {
          buy_price: buyPrice === '' ? null : Number(buyPrice),
          sell_price: sellPrice === '' ? null : Number(sellPrice),
          unit_of_measure: formValues.unitOfMeasure as OrderUnitOfMeasure,
        });

        toasterStore.push(
          alert(
            t('administration.material_rates.material_rate_updated_message'),
            AlertTypes.success,
          ),
        );

        setEditingRateId(null);
        refetchCurrentScope();
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.material_rates.failed_to_update_material_rate'),
        );
      } finally {
        setIsSavingInlineEdit(false);
      }
    },
    [
      inlineEditForm.clearErrors,
      inlineEditForm.getValues,
      inlineEditForm.setError,
      refetchCurrentScope,
      toasterStore,
      updateMaterialRate,
    ],
  );

  const confirmDelete = useCallback(async () => {
    if (!rateToDelete) {
      return;
    }

    setIsDeleting(true);

    try {
      await deleteMaterialRate(rateToDelete.id);

      toasterStore.push(
        alert(
          t('administration.material_rates.material_rate_deleted_message'),
          AlertTypes.success,
        ),
      );

      setRateToDelete(null);
      refetchCurrentScope();
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.material_rates.failed_to_delete_material_rate'),
      );
    } finally {
      setIsDeleting(false);
    }
  }, [deleteMaterialRate, rateToDelete, refetchCurrentScope, toasterStore]);

  const openAddRateDialog = useCallback(() => {
    setAddRateDialogState({ errors: undefined, isOpen: true, isSaving: false });
  }, []);

  const closeAddRateDialog = useCallback(() => {
    setAddRateDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleAddRateSubmit = useCallback(async () => {
    const formData = await materialRateFormRef.current?.submit();

    if (!formData) {
      return;
    }

    setAddRateDialogState((state) => ({ ...state, isSaving: true }));

    try {
      await createMaterialRate({
        buy_price: materialRatePriceToPayload(formData.buyPrice),
        customer_account_id: formData.customer?.id || undefined,
        material_id: formData.material.id,
        sell_price: materialRatePriceToPayload(formData.sellPrice),
        site_id: site.id,
        unit_of_measure: formData.unitOfMeasure.id as OrderUnitOfMeasure,
      });

      toasterStore.push(
        alert(
          t('administration.material_rates.material_rate_created_message'),
          AlertTypes.success,
        ),
      );

      setAddRateDialogState({ isOpen: false, isSaving: false });

      // A rate created for a customer while viewing another scope would vanish
      // on refetch — follow it by switching the filter to that customer instead
      // (the filter-change effect performs the refetch).
      const createdCustomer = formData.customer?.id ? formData.customer : null;
      const liveCustomerId = filterForm.getValues('customer')?._entity.id ?? null;

      if (createdCustomer?.id && createdCustomer.id !== liveCustomerId) {
        filterForm.setValue('customer', {
          _entity: new AccountTypeahead(
            createdCustomer.id,
            createdCustomer.name ?? '',
            null,
            false,
          ),
          label: createdCustomer.name ?? '',
          value: createdCustomer.id,
        });
      } else {
        refetchCurrentScope();
      }
    } catch (error) {
      const e = error as AxiosError<ModelError_Response>;
      let badRequestErrors: ModelError_Item[] | undefined;

      if (e.response?.status === 422) {
        badRequestErrors = e.response?.data.error.errors;
      }

      setAddRateDialogState((state) => ({
        ...state,
        errors: badRequestErrors,
        isSaving: false,
      }));

      connection.handleRequestError(
        error,
        t('error_messages.material_rates.failed_to_create_material_rate'),
        [422],
      );
    }
  }, [
    companyId,
    createMaterialRate,
    filterForm.getValues,
    filterForm.setValue,
    refetchCurrentScope,
    site.id,
    toasterStore,
  ]);

  const handleInlineEditKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        // The Site form's dialog also listens for Escape — without this it would
        // close the whole form instead of just cancelling the row edit.
        event.stopPropagation();
        cancelInlineEdit();
      }
    },
    [cancelInlineEdit],
  );

  const gridTemplateColumns =
    'minmax(160px, 1.8fr) minmax(110px, 1.2fr) 110px 110px 110px 84px';

  const renderScopeCell = (rate: MaterialRate) => {
    if (rate.customerAccount) {
      return (
        <Box sx={{ minWidth: 0 }}>
          <Chip
            label={t('administration.material_rates.site_panel.customer_scope_chip')}
            size="small"
            variant="outlined"
          />
          <Typography
            noWrap
            sx={{ color: 'text.secondary', fontSize: '11.5px', mt: 0.25 }}
          >
            {rate.customerAccount.name}
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Chip
          color="info"
          label={t('administration.material_rates.site_panel.default_scope_chip')}
          size="small"
          variant="outlined"
        />
      </Box>
    );
  };

  const renderPriceCell = (price: number | null) => (
    <Typography
      sx={{
        color: isNil(price) ? 'text.secondary' : 'text.primary',
        fontSize: '13.5px',
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 600,
        textAlign: 'right',
      }}
    >
      {isNil(price) ? '—' : currencyFormatter(price)}
    </Typography>
  );

  const renderReadRow = (rate: MaterialRate) => (
    <Box
      key={rate.id}
      sx={(theme) => ({
        '&:hover': { backgroundColor: theme.palette.action.hover },
        alignItems: 'center',
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns,
        px: 1.5,
        py: 1,
      })}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '13.5px', fontWeight: 600 }}>
          {rate.material.name}
        </Typography>
      </Box>

      {renderScopeCell(rate)}

      <Typography sx={{ fontSize: '13px' }}>{rate.unitOfMeasure}</Typography>

      {renderPriceCell(rate.buyPrice)}
      {renderPriceCell(rate.sellPrice)}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {permissions.canEditMaterialRate && (
          <IconButton
            aria-label={t('administration.material_rates.site_panel.edit_rate')}
            onClick={() => startInlineEdit(rate)}
            size="small"
          >
            <Edit fontSize="inherit" />
          </IconButton>
        )}
        {permissions.canDeleteMaterialRate && (
          <IconButton
            aria-label={t('administration.material_rates.site_panel.remove_rate')}
            onClick={() => setRateToDelete(rate)}
            size="small"
          >
            <Delete fontSize="inherit" />
          </IconButton>
        )}
      </Box>
    </Box>
  );

  const renderEditRow = (rate: MaterialRate) => (
    <Box
      key={rate.id}
      onKeyDown={handleInlineEditKeyDown}
      sx={(theme) => ({
        alignItems: 'center',
        backgroundColor: theme.palette.action.selected,
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns,
        px: 1.5,
        py: 1,
      })}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '13.5px', fontWeight: 600 }}>
          {rate.material.name}
        </Typography>
      </Box>

      {renderScopeCell(rate)}

      <SelectInput
        control={inlineEditForm.control}
        name="unitOfMeasure"
        options={unitOfMeasureOptions.map((option) => ({
          label: option.name,
          value: option.id,
        }))}
      />

      <NumberInput
        control={inlineEditForm.control}
        slotProps={{ htmlInput: { min: 0, step: '0.01', style: { textAlign: 'right' } } }}
        name="buyPrice"
        placeholder="0.00"
      />

      <NumberInput
        control={inlineEditForm.control}
        slotProps={{ htmlInput: { min: 0, step: '0.01', style: { textAlign: 'right' } } }}
        name="sellPrice"
        placeholder="0.00"
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton
          aria-label={t('actions.save')}
          disabled={isSavingInlineEdit}
          onClick={() => saveInlineEdit(rate)}
          size="small"
          sx={{ color: 'success.main' }}
        >
          <Check fontSize="inherit" />
        </IconButton>
        <IconButton
          aria-label={t('actions.cancel')}
          disabled={isSavingInlineEdit}
          onClick={cancelInlineEdit}
          size="small"
        >
          <Close fontSize="inherit" />
        </IconButton>
      </Box>
    </Box>
  );

  const renderLoadingRows = () => (
    <>
      {[0, 1, 2].map((skeletonIndex) => (
        <Box
          key={skeletonIndex}
          sx={(theme) => ({
            alignItems: 'center',
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns,
            px: 1.5,
            py: 1.25,
          })}
        >
          <Skeleton width="80%" />
          <Skeleton width="60%" />
          <Skeleton width="50%" />
          <Skeleton width="70%" />
          <Skeleton width="70%" />
          <Box />
        </Box>
      ))}
    </>
  );

  const renderEmptyState = () => (
    <Box
      sx={(theme) => ({
        borderTop: `1px solid ${theme.palette.divider}`,
        px: 2,
        py: 3,
        textAlign: 'center',
      })}
    >
      <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
        {selectedCustomer
          ? t('administration.material_rates.site_panel.empty_customer_title', {
              customer: selectedCustomer.name,
            })
          : t('administration.material_rates.site_panel.empty_default_title')}
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '12.5px', mt: 0.5 }}>
        {selectedCustomer
          ? t('administration.material_rates.site_panel.empty_customer_body')
          : t('administration.material_rates.site_panel.empty_default_body')}
      </Typography>
    </Box>
  );

  if (ratesAreHiddenForThisUser) {
    return (
      <Box sx={{ gridColumn: '1 / -1' }}>
        <Typography variant="form-section-title">
          {t('administration.material_rates.title')}
        </Typography>

        <Box
          sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            mt: 1,
            px: 2,
            py: 3,
            textAlign: 'center',
          })}
        >
          <Typography sx={{ color: 'text.secondary', fontSize: '14px', fontWeight: 600 }}>
            {t('administration.material_rates.site_panel.hidden_title')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '12.5px', mt: 0.5 }}>
            {t('administration.material_rates.site_panel.hidden_body')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ gridColumn: '1 / -1' }}>
      <Box
        sx={{
          alignItems: 'flex-end',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="form-section-title">
          {t('administration.material_rates.title')}
          {/* A capped fetch with zero survivors proved nothing about the true
              count — showing "(0)" would contradict the cap notice below. */}
          {!listState.isLoading &&
            !(listState.isCapped && !listState.rates.length) &&
            ` (${listState.rates.length})`}
        </Typography>

        <Box sx={{ width: 260 }}>
          <AsyncAutocompleteField
            control={filterForm.control}
            fetchFn={customersFetchFn}
            label={t('form_fields.customer')}
            name="customer"
            parseFn={customerParseFn}
            queryName={`SiteMaterialRates_${site.id}_Customers`}
          />
        </Box>
      </Box>

      <Typography sx={{ color: 'text.secondary', fontSize: '12.5px', mb: 1, mt: 0.5 }}>
        {selectedCustomer
          ? t('administration.material_rates.site_panel.showing_customer', {
              customer: selectedCustomer.name,
            })
          : t('administration.material_rates.site_panel.showing_defaults')}
      </Typography>

      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: 'hidden',
        })}
      >
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 700 }}>
            <Box
              sx={(theme) => ({
                backgroundColor: theme.palette.action.hover,
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns,
                px: 1.5,
                py: 1,
              })}
            >
              <Typography sx={{ fontSize: '11.5px', fontWeight: 700 }}>
                {t('form_fields.material')}
              </Typography>
              <Typography sx={{ fontSize: '11.5px', fontWeight: 700 }}>
                {t('form_fields.scope')}
              </Typography>
              <Typography sx={{ fontSize: '11.5px', fontWeight: 700 }}>
                {t('form_fields.unit')}
              </Typography>
              <Typography
                sx={{ fontSize: '11.5px', fontWeight: 700, textAlign: 'right' }}
              >
                {t('administration.material_rates.buy_price')}
              </Typography>
              <Typography
                sx={{ fontSize: '11.5px', fontWeight: 700, textAlign: 'right' }}
              >
                {t('administration.material_rates.sell_price')}
              </Typography>
              <Box />
            </Box>

            {listState.isLoading
              ? renderLoadingRows()
              : listState.rates.length
                ? listState.rates.map((rate) =>
                    rate.id === editingRateId ? renderEditRow(rate) : renderReadRow(rate),
                  )
                : // A capped fetch proves nothing about emptiness — the cap notice
                  // below renders instead of a possibly-false "no rates".
                  !listState.isCapped && renderEmptyState()}

            {!listState.isLoading && listState.isCapped && (
              <Box
                sx={(theme) => ({
                  backgroundColor: theme.palette.action.hover,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  px: 1.5,
                  py: 1,
                })}
              >
                <Typography sx={{ fontSize: '12px' }}>
                  {listState.rates.length
                    ? t('administration.material_rates.site_panel.cap_notice', {
                        count: listState.rates.length,
                      })
                    : t('administration.material_rates.site_panel.cap_notice_empty')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {permissions.canCreateMaterialRate && (
        <Button
          color="info"
          onClick={openAddRateDialog}
          size="small"
          sx={{ mt: 1, textDecoration: 'underline' }}
          variant="text"
        >
          {t('administration.material_rates.site_panel.add_cta')}
        </Button>
      )}

      <Typography sx={{ color: 'text.secondary', fontSize: '11.5px', mt: 0.5 }}>
        {t('administration.material_rates.site_panel.project_rates_note')}
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '11.5px', mt: 0.25 }}>
        {t('administration.material_rates.site_panel.save_semantics_note')}
      </Typography>

      <Dialog
        isOpen={addRateDialogState.isOpen}
        onClose={() => {
          if (!addRateDialogState.isSaving) {
            closeAddRateDialog();
          }
        }}
        preventCloseOnBackdropClick
        sx={{ [`& .${paperClasses.root}`]: { maxWidth: '500px', overflowY: 'visible' } }}
      >
        <DialogHeader
          onClose={() => {
            if (!addRateDialogState.isSaving) {
              closeAddRateDialog();
            }
          }}
        >
          <Box>
            {t('administration.material_rates.add_rate_form_title')}
            <Typography sx={{ color: 'text.secondary', fontSize: '12px' }}>
              {selectedCustomer ? `${site.name} · ${selectedCustomer.name}` : site.name}
            </Typography>
          </Box>
        </DialogHeader>

        <DialogContent>
          {addRateDialogState.isOpen && (
            <MaterialRateForm
              ref={materialRateFormRef}
              companyId={companyId}
              customer={selectedCustomer ?? undefined}
              errors={addRateDialogState.errors}
              site={site}
              suppressProject
            />
          )}
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={addRateDialogState.isSaving}
            onClick={closeAddRateDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="brandV2Yellow"
            disabled={addRateDialogState.isSaving}
            loading={addRateDialogState.isSaving}
            onClick={handleAddRateSubmit}
          >
            {t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        isBusy={isDeleting}
        isOpen={Boolean(rateToDelete)}
        onClose={() => setRateToDelete(null)}
        onConfirm={confirmDelete}
        title={t('administration.material_rates.site_panel.delete_title')}
      >
        {rateToDelete && (
          <Typography>
            {t('administration.material_rates.site_panel.delete_confirmation', {
              material: rateToDelete.material.name,
              scope:
                rateToDelete.customerAccount?.name ??
                t('administration.material_rates.site_panel.default_scope_chip'),
              site: site.name,
            })}
          </Typography>
        )}
      </ConfirmationDialog>
    </Box>
  );
});

export default SiteMaterialRatesSection;
